/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React, { useEffect, useState } from "react"
import { Spacer } from "@cd2/components"
import { useTheme } from "@cd2/theme"
import { motion } from "framer-motion"
import { useMediaBreakpoints } from "../hooks/useMediaBreakpoints"
import { Link, useLocation } from "react-router-dom"
import { eRoutes } from "../routes"

interface ISessionWrapperProps {
  children: React.ReactNode
  cta?: "sign-in" | "sign-up"
}

declare global {
  interface Window {
    SESSION_WRAPPER?: {
      setWidth: (width: number) => void
    }
  }
}

export function SessionWrapper({ children, cta }: ISessionWrapperProps) {
  const { themeColor } = useTheme()
  const { isMobile } = useMediaBreakpoints()
  const location = useLocation()

  const [width, setWidth] = useState(500)
  const onCaseCoaching = location.pathname.includes("/case-coaching/")

  useEffect(() => {
    window.SESSION_WRAPPER = { setWidth }
    return () => {
      delete window.SESSION_WRAPPER
    }
  }, [])

  return (
    <div
      css={css`
        background: white;
        margin: 0 auto;
        min-height: 100vh;
        padding: 0 px 64px;
      `}
    >
      <motion.div
        initial={{ paddingTop: "calc(25vh)" }}
        animate={{ paddingTop: `calc(${isMobile ? 5 : 10}vh)` }}
        transition={{ type: "spring", mass: 3, stiffness: 75, damping: 300 }}
        onCompositionEnd={() => console.warn("done")}
      >
        <div
          css={css`
            max-width: ${onCaseCoaching ? 900 : width}px;
            margin: 0 auto;
          `}
        >
          <div
            css={css`
              color: ${themeColor("primary")};
              font-size: 32px;
              font-weight: 400;
              text-align: center;
            `}
          >
            CLEVERGOOSE
          </div>
          <Spacer size={16} />
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", mass: 3, stiffness: 75, damping: 300, delay: 0.5 }}
          >
            <div
              css={css`
                border: 1px solid #edeff1;
                background: #fafafb;
                padding: 32px;
                border-radius: 16px;
              `}
            >
              {children}
            </div>
          </motion.div>
        </div>
        <div
          css={css`
            text-align: center;
            margin-top: 32px;

            .cta {
              font-size: 16px;
              font-weight: 500;
            }
            .copy {
              font-size: 12px;
              color: #343434;
            }
          `}
        >
          {cta === "sign-up" && (
            <div className="cta">
              {`Don't have an account?`} <Link to={eRoutes["/sign-up"]}>Register here.</Link>
            </div>
          )}
          {cta === "sign-in" && (
            <div className="cta">
              {`Already have an account?`} <Link to={eRoutes["/sign-in"]}>Sign in here.</Link>
            </div>
          )}
          <Spacer size={16} />
          <div className="copy">
            &copy; {new Date().getFullYear()} CleverGoose. All rights reserved.
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export function SessionWrapperText({ heading, body }: { heading: string; body: string }) {
  return (
    <div>
      <h1
        css={css`
          font-size: 24px;
          text-align: center;
          margin: 0;
        `}
      >
        {heading}
      </h1>
      <p
        css={css`
          font-size: 16px;
        `}
      >
        {body}
      </p>
    </div>
  )
}
